import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { STREAM_DEFAULT_SETTINGS } from "../constants";
import {
  ArcwareApplication,
  ArcwareConfigParams,
  ArcwareInit,
} from "@arcware-cloud/pixelstreaming-websdk";
import { DISABLE_STREAM } from "../config";

export type UseArcwareStreamResult = {
  connected: boolean;
  mount: boolean;
  streamRef: RefObject<ArcwareApplication | null>;
  emitUIInteraction: (value: any) => void;
};

// Resolve the SDK's <video> element via the public controller chain; fall back
// to the SDK-internal marker and a DOM query. Never cache the result — the SDK
// destroys/recreates the element on reconnect.
export function getStreamVideoElement(
  app: ArcwareApplication | null,
): HTMLVideoElement | null {
  if (!app) return null;
  const anyApp = app as any;
  return (
    anyApp.stream?.webRtcController?.videoPlayer?.getVideoElement?.() ??
    anyApp.stream?._rVFCArmedForElement ??
    app.rootElement?.querySelector?.("video") ??
    null
  );
}

export type UseArcwareStreamArgs = {
  containerId: string;
  shareId: string;
  onVideoInitialized?: () => void;
  onAppResponse?: (res: any, api: UseArcwareStreamResult) => void;
  keysMapper?: Record<string, string>;
  valuesMapper?: Record<string, string>;
} & Pick<ArcwareConfigParams, "settings">;

export function useArcwareStream(
  props: UseArcwareStreamArgs,
): UseArcwareStreamResult {
  const {
    containerId,
    shareId,
    onVideoInitialized,
    onAppResponse,
    keysMapper = {} as Record<string, string>,
    valuesMapper = {} as Record<string, string>,
  } = props;
  const didMountRef = useRef(false);

  const streamRef = useRef<ArcwareApplication | null>(null);
  const [connected, setConnected] = useState(false);
  const [mount, setMount] = useState(false);

  // Keep mappers in refs so emitUIInteraction can stay referentially stable
  // (App passes fresh default objects each render).
  const keysMapperRef = useRef(keysMapper);
  keysMapperRef.current = keysMapper;
  const valuesMapperRef = useRef(valuesMapper);
  valuesMapperRef.current = valuesMapper;
  // Ref so initializeStream stays stable (App passes a fresh arrow each render).
  const onVideoInitializedRef = useRef(onVideoInitialized);
  onVideoInitializedRef.current = onVideoInitialized;

  // Outgoing messages emitted before the WebRTC data channel is open would be
  // silently dropped by the SDK (DataChannelSender only sends when readyState
  // === "open"). Queue them and flush once the channel is ready so no command
  // — most importantly change_model — is ever lost.
  const queueRef = useRef<Record<string, any>[]>([]);
  // After a network drop the SDK never closes the old RTCPeerConnection, so
  // its data channel can keep reporting readyState "open" for a while — sends
  // would vanish into the dead channel. Only trust canSend() once the CURRENT
  // connection confirmed its channel (dataChannelOpen / video ready).
  const channelLiveRef = useRef(false);
  // "ready" already fired for the current connection (dedupes the two
  // video-init signals below).
  const readyRef = useRef(false);
  const flushTimerRef = useRef<number | null>(null);

  const canSend = useCallback((): boolean => {
    if (!channelLiveRef.current) return false;
    try {
      const ds = (streamRef.current as any)?.webRtcController?.dataChannelSender;
      return !!(ds && ds.canSend());
    } catch {
      return false;
    }
  }, []);

  const sendMapped = useCallback((mapped: Record<string, any>) => {
    console.log("emitUIInteraction:", mapped);
    streamRef.current?.emitUIInteraction(mapped);
  }, []);

  const flushQueue = useCallback(() => {
    if (!streamRef.current || !canSend()) return;
    const q = queueRef.current;
    if (!q.length) return;
    queueRef.current = [];
    q.forEach(sendMapped);
  }, [canSend, sendMapped]);

  // Keep draining until the queue is empty. A fixed retry budget (the old
  // 25×200ms) starved after reconnects when the new data channel opened late
  // (fresh instance booting) — queued change_models were then lost forever.
  const ensureFlushLoop = useCallback(() => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = window.setInterval(() => {
      if (!queueRef.current.length || !streamRef.current) {
        window.clearInterval(flushTimerRef.current!);
        flushTimerRef.current = null;
        return;
      }
      flushQueue();
    }, 250);
  }, [flushQueue]);

  const emitUIInteraction = useCallback(
    (value: any) => {
      const mapped = Object.entries(value).reduce<Record<string, any>>(
        (acc, [k, v]) => {
          const mappedKey = keysMapperRef.current[k] || k;
          acc[mappedKey] =
            typeof v === "string" ? valuesMapperRef.current[v] || v : v;
          return acc;
        },
        {},
      );

      if (streamRef.current && canSend()) {
        flushQueue(); // preserve FIFO ordering with anything queued earlier
        sendMapped(mapped);
      } else {
        // Channel not ready yet — buffer and deliver on flush.
        queueRef.current.push(mapped);
        ensureFlushLoop();
      }
    },
    [canSend, flushQueue, sendMapped, ensureFlushLoop],
  );

  const api = {
    mount,
    connected,
    streamRef,
    emitUIInteraction,
  };

  const initializeStream = useCallback(() => {
    if (streamRef.current) return;

    const Init = ArcwareInit(
      { shareId },
      {
        ...STREAM_DEFAULT_SETTINGS,
      },
      true,
    );

    const Application = Init.Application;
    streamRef.current = Application;
    (window as any).ArcwareApp = Application;

    // ready — Arcware's videoInitializedHandler never re-fires after the
    // SDK's silent auto-reconnect (its internal once-guard is only reset by
    // the manual reconnect path), so also listen to the Epic-level
    // "videoInitialized" event; both funnel into one per-connection handler.
    const handleReady = () => {
      if (readyRef.current) return;
      readyRef.current = true;
      channelLiveRef.current = true;
      setConnected(true);
      flushQueue();
      ensureFlushLoop();
      onVideoInitializedRef.current?.();
    };
    Application.stream.videoInitializedHandler.add(handleReady);
    (Application.stream as any).addEventListener("videoInitialized", handleReady);

    // Fires per (re)connection once the fresh data channel is writable — the
    // earliest safe moment to deliver anything buffered.
    (Application.stream as any).addEventListener("dataChannelOpen", () => {
      channelLiveRef.current = true;
      flushQueue();
      ensureFlushLoop();
    });

    Application.getApplicationResponse((response) => {
      onAppResponse?.(response, api);
    });

    // disconnected handler
    (Application.stream as any).addEventListener("webRtcDisconnected", () => {
      readyRef.current = false;
      channelLiveRef.current = false;
      // Commands queued for the dead session are stale — the reconnect flow
      // re-emits the full state (change_model + config sync) anyway.
      queueRef.current = [];
      setConnected(false);
    });

    // mount Arcware canvas container
    const container = document.getElementById(containerId);
    if (container && Application.rootElement) {
      container.appendChild(Application.rootElement);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerId, shareId, flushQueue, ensureFlushLoop]);

  // mount
  useEffect(() => {
    if (DISABLE_STREAM) return;

    initializeStream();
    didMountRef.current = true;
    setTimeout(() => {
      setMount(true);
    }, 50);
  }, [initializeStream]);

  return api;
}
